import {
    Box,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    Stack,
    Typography,
} from '@mui/material';
import { createElement, FunctionComponent, useMemo } from 'react';
import { ResolvedWidget } from '@/pages/widgetEditor/types';
import {
    SAMPLE_DATA_ATTRIBUTE,
    buildAttributes,
    humanizeLabel,
    PageConfigElement,
} from '@/pages/widgetEditor/utils';
import { useFontLinks } from '@/pages/widgetEditor/hooks/useFontLinks';
import { useWidgetEntry } from '@/pages/widgetEditor/hooks/useWidgetEntry';

interface WidgetPreviewCardProps {
    resolvedWidgets: ResolvedWidget[];
    /** Absolute URL of the selected version's module entry. */
    entryUrl?: string;
    pageConfig?: PageConfigElement;
}

/** Live preview: one row per added instance, rendered as its custom element. */
export const WidgetPreviewCard: FunctionComponent<WidgetPreviewCardProps> = ({
    resolvedWidgets,
    entryUrl,
    pageConfig,
}) => {
    const usedTags = useMemo(
        () => resolvedWidgets.map(({ widget }) => widget.tag),
        [resolvedWidgets]
    );

    const scriptStateFor = useWidgetEntry(entryUrl, usedTags);

    // Distinct font-url values across every instance, so the preview matches
    // what the generated snippet will load for a customer.
    const fontHrefs = useMemo(
        () =>
            Array.from(
                new Set(
                    resolvedWidgets
                        .map(
                            ({ widget, config }) =>
                                buildAttributes(widget, config)['font-url']
                        )
                        .filter((href): href is string => Boolean(href))
                )
            ),
        [resolvedWidgets]
    );
    useFontLinks(fontHrefs);

    return (
        <Card>
            {/*
             * In the DOM before the entry script loads, which reads it once,
             * just as the host page's own copy is.
             */}
            {pageConfig && createElement(pageConfig.tag, pageConfig.attributes)}
            <CardContent
                sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 1,
                    flexWrap: 'wrap',
                }}
            >
                <Typography variant='h6'>Vorschau</Typography>
                {resolvedWidgets.length > 0 && (
                    <Typography color='text.secondary' variant='caption'>
                        Kontodaten in der Vorschau stammen aus einem Testkonto.
                    </Typography>
                )}
            </CardContent>
            <Divider sx={{ width: '100%' }} />

            {resolvedWidgets.length === 0 ? (
                <Box
                    sx={{
                        p: 4,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: 160,
                        bgcolor: 'grey.50',
                    }}
                >
                    <Typography color='text.secondary' variant='body2'>
                        Füge ein Widget hinzu, um die Vorschau zu sehen.
                    </Typography>
                </Box>
            ) : (
                <Stack divider={<Divider />}>
                    {resolvedWidgets.map(({ instance, widget, config }) => {
                        const state = scriptStateFor(widget.tag);

                        /*
                         * Every attribute with a concrete value, unlike the
                         * embed snippet: the preview must render exactly what
                         * the widget will look like, defaults included.
                         */
                        const attributes = {
                            ...buildAttributes(widget, config),
                            [SAMPLE_DATA_ATTRIBUTE]: '',
                        };

                        return (
                            <Box
                                key={instance.instanceId}
                                sx={{
                                    p: 4,
                                    bgcolor: 'grey.50',
                                }}
                            >
                                <Typography
                                    color='text.secondary'
                                    variant='caption'
                                    sx={{
                                        display: 'block',
                                        mb: 2,
                                    }}
                                >
                                    {humanizeLabel(widget.widget)}
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        minHeight: 120,
                                    }}
                                >
                                    {state === 'error' ? (
                                        <Typography
                                            color='error'
                                            variant='body2'
                                        >
                                            Vorschau konnte nicht geladen
                                            werden.
                                        </Typography>
                                    ) : state === 'stale' ? (
                                        <Typography
                                            color='text.secondary'
                                            variant='body2'
                                        >
                                            Die Vorschau zeigt noch die zuvor
                                            gewählte Version. Lade die Seite
                                            neu, um diese Version zu sehen.
                                        </Typography>
                                    ) : (
                                        <>
                                            {/*
                                             * Rendered while still loading:
                                             * the entry only fetches a
                                             * widget's code once it finds the
                                             * tag on the page.
                                             */}
                                            {state === 'loading' && (
                                                <CircularProgress size={24} />
                                            )}
                                            {createElement(
                                                widget.tag,
                                                attributes
                                            )}
                                        </>
                                    )}
                                </Box>
                            </Box>
                        );
                    })}
                </Stack>
            )}
        </Card>
    );
};
